import { useEffect, useState } from 'react'

/**
 * Hook that defers showing a loading state for a specified delay.
 * This prevents flickering when loading completes quickly.
 *
 * Unlike useDebounce, this returns false immediately when isLoading becomes false,
 * but only returns true after the delay when isLoading becomes true.
 *
 * @param isLoading - Whether the content is currently loading
 * @param delay - Delay in milliseconds before showing loading state (default: 300ms)
 * @returns Whether to show the loading indicator
 */
export function useDeferredLoading(isLoading: boolean, delay = 300): boolean {
  const [showLoading, setShowLoading] = useState(false)

  useEffect(() => {
    if (!isLoading) {
      // Immediately hide loading when done
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowLoading(false)
      return
    }

    // Delay showing loading to prevent flicker on quick loads
    const timeoutId = setTimeout(() => {
      setShowLoading(true)
    }, delay)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isLoading, delay])

  return showLoading
}
